import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { brokerProfileSchema, type BrokerProfile } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient, getCsrfHeaders } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { X, CheckCircle, AlertCircle, Loader2, Search } from "lucide-react";
import { SignupHeader } from "@/components/SignupHeader";
import { useCsrfToken } from "@/hooks/use-csrf-token";

interface FMCSACarrierInfo {
  dotNumber: string;
  legalName: string;
  dbaName?: string;
  mcNumber?: string;
  phone?: string;
  allowedToOperate: boolean;
}

interface VerificationResult {
  success: boolean;
  carrier?: FMCSACarrierInfo;
  error?: string;
  errors?: string[];
  verified: boolean;
  allowedToOperate: boolean;
}

const EQUIPMENT_SPECIALIZATION_OPTIONS = [
  "Flatbed",
  "Refrigerated / Reefer",
  "Hazmat",
  "Expedited",
  "Partial Truckload (PTL)",
  "High Value Goods",
  "Hotshot / Same Day",
  "Cross-Border",
];

const COMMODITY_OPTIONS = [
  "Food Grade",
  "Pharmaceuticals",
  "High Value Goods",
  "Construction Materials",
  "Auto Parts",
  "Electronics",
  "Agricultural",
];

const REGION_OPTIONS = [
  "Northeast",
  "Southeast",
  "Midwest",
  "Southwest",
  "West Coast",
  "Pacific Northwest",
  "Great Lakes",
  "Gulf Coast",
];

const YEARS_IN_BUSINESS_OPTIONS: { label: string; value: number }[] = [
  { label: "Less than 1 year", value: 0 },
  { label: "1–3 years", value: 2 },
  { label: "3–5 years", value: 4 },
  { label: "5–10 years", value: 7 },
  { label: "10+ years", value: 15 },
];

export default function SignupBroker() {
  useCsrfToken();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [specializations, setSpecializations] = useState<string[]>([]);
  const [commoditySpecializations, setCommoditySpecializations] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [shippingLanes, setShippingLanes] = useState<string[]>([]);
  const [laneInput, setLaneInput] = useState("");
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual" | "free" | null>(null);
  const [membershipTier, setMembershipTier] = useState<string>("Paid");
  const [verificationStatus, setVerificationStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [verificationResult, setVerificationResult] = useState<VerificationResult | null>(null);
  const setupSessionAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      setupSessionAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billing = params.get("billing") as "monthly" | "annual" | "free" | null;
    const tier = params.get("tier");
    if (billing === "monthly" || billing === "annual" || billing === "free") {
      setBillingCycle(billing);
      if (tier === "SearchAndBid" || tier === "Paid") setMembershipTier(tier);
    } else {
      toast({
        title: "Invalid access",
        description: "Please select a membership plan first",
        variant: "destructive",
      });
      setLocation("/select-membership/broker");
    }
  }, [toast, setLocation]);

  const form = useForm<BrokerProfile>({
    resolver: zodResolver(brokerProfileSchema),
    defaultValues: {
      userType: "Broker",
      company: "",
      phone: "",
      dotNumber: "",
      mcNumber: "",
      yearsInBusiness: undefined,
      specializations: [],
      commoditySpecializations: [],
      operatingRegions: [],
      shippingLanes: [],
      hasBrokerBond: undefined as unknown as true,
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: BrokerProfile) => {
      if (!billingCycle) {
        throw new Error("Billing cycle not selected");
      }
      const searchParams = new URLSearchParams(window.location.search);
      const billing = searchParams.get("billing") ?? "monthly";
      await apiRequest("POST", "/api/profile/complete", {
        ...data,
        specializations,
        commoditySpecializations,
        operatingRegions: regions,
        shippingLanes,
        billingCycle,
        billing,
        membershipTier,
      });
    },
    onSuccess: async () => {
      if (window.fbq) window.fbq('track', 'CompleteRegistration');
      if (billingCycle === "free") {
        await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
        setLocation("/dashboard");
        return;
      }
      try {
        const controller = new AbortController();
        setupSessionAbortRef.current = controller;
        const res = await fetch("/api/create-setup-session", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
          body: JSON.stringify({ billingCycle: billingCycle || "monthly", membershipTier }),
          signal: controller.signal,
        });
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
          return;
        }
        throw new Error("No checkout URL returned");
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        console.error("Failed to create Stripe setup session:", err);
        toast({
          title: "Payment Setup Required",
          description: "We couldn't set up payment collection. Please try again. A credit card is required to complete signup.",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const verifyBroker = async () => {
    const dotNumber = form.getValues("dotNumber");
    const mcNumber = form.getValues("mcNumber");

    if (!dotNumber && !mcNumber) {
      toast({
        title: "Missing Information",
        description: "Please enter a DOT or MC number to verify",
        variant: "destructive",
      });
      return;
    }

    setVerificationStatus("loading");
    setVerificationResult(null);

    try {
      const response = await fetch("/api/carriers/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        body: JSON.stringify({ dotNumber, mcNumber, role: "Broker" }),
      });

      const result: VerificationResult = await response.json();
      setVerificationResult(result);

      if (result.success && result.carrier) {
        setVerificationStatus("success");
        form.setValue("company", result.carrier.legalName);
        if (result.carrier.mcNumber) form.setValue("mcNumber", result.carrier.mcNumber);
        if (result.carrier.dotNumber) form.setValue("dotNumber", result.carrier.dotNumber);
        if (result.carrier.phone) form.setValue("phone", result.carrier.phone);
        toast({
          title: "Broker Verified",
          description: `Found: ${result.carrier.legalName}`,
        });
      } else {
        setVerificationStatus("error");
        toast({
          title: "Verification Failed",
          description: result.error || "Could not verify broker credentials",
          variant: "destructive",
        });
      }
    } catch {
      setVerificationStatus("error");
      toast({
        title: "Verification Error",
        description: "Failed to connect to verification service",
        variant: "destructive",
      });
    }
  };

  const toggleSpecialization = (spec: string) => {
    setSpecializations((prev) => {
      const updated = prev.includes(spec) ? prev.filter((s) => s !== spec) : [...prev, spec];
      form.setValue("specializations", updated, { shouldValidate: true });
      return updated;
    });
  };

  const toggleCommoditySpecialization = (spec: string) => {
    setCommoditySpecializations((prev) => {
      const updated = prev.includes(spec) ? prev.filter((s) => s !== spec) : [...prev, spec];
      form.setValue("commoditySpecializations", updated, { shouldValidate: true });
      return updated;
    });
  };

  const toggleRegion = (region: string) => {
    setRegions((prev) => {
      const updated = prev.includes(region) ? prev.filter((r) => r !== region) : [...prev, region];
      form.setValue("operatingRegions", updated, { shouldValidate: true });
      return updated;
    });
  };

  const addShippingLane = () => {
    if (laneInput.trim() && !shippingLanes.includes(laneInput.trim())) {
      setShippingLanes([...shippingLanes, laneInput.trim()]);
      setLaneInput("");
    }
  };

  const removeShippingLane = (lane: string) => {
    setShippingLanes(shippingLanes.filter((l) => l !== lane));
  };

  if (!billingCycle) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <SignupHeader backUrl="/select-membership/broker" />
      <div className="flex items-center justify-center p-4 pt-24 min-h-screen">
        <Card className="max-w-2xl w-full">
          <CardHeader>
            <CardTitle className="text-2xl">Complete Your Broker Profile</CardTitle>
            <CardDescription>
              Set up your broker profile to connect shippers and carriers efficiently
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit((data) => mutation.mutate(data))} className="space-y-0">

                {/* Section 1 — Business Information */}
                <div className="border-t pt-5 pb-5 mb-5 space-y-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Business Information</p>

                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="XYZ Freight Brokerage LLC" {...field} data-testid="input-company" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number *</FormLabel>
                        <FormControl>
                          <Input placeholder="(555) 123-4567" {...field} data-testid="input-phone" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="yearsInBusiness"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Years in Business</FormLabel>
                        <Select
                          onValueChange={(val) => field.onChange(parseInt(val))}
                          value={field.value !== undefined ? String(field.value) : ""}
                        >
                          <FormControl>
                            <SelectTrigger data-testid="select-years">
                              <SelectValue placeholder="Select a range" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {YEARS_IN_BUSINESS_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={String(opt.value)}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Section 2 — Credentials & Verification */}
                <div className="border-t pt-5 pb-5 mb-5 space-y-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Credentials &amp; Verification</p>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="dotNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>DOT Number *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="1234567"
                              {...field}
                              data-testid="input-dot"
                              onChange={(e) => {
                                field.onChange(e);
                                setVerificationStatus("idle");
                                setVerificationResult(null);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="mcNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>MC Number</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="123456"
                              {...field}
                              data-testid="input-mc"
                              onChange={(e) => {
                                field.onChange(e);
                                setVerificationStatus("idle");
                                setVerificationResult(null);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Click <span className="font-medium">Verify with FMCSA</span> to instantly confirm your credentials and speed up your account approval.
                  </p>

                  <div className="flex items-center gap-3 flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={verifyBroker}
                      disabled={verificationStatus === "loading"}
                      data-testid="button-verify-broker"
                    >
                      {verificationStatus === "loading" ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <Search className="w-4 h-4 mr-2" />
                          Verify with FMCSA
                        </>
                      )}
                    </Button>

                    {verificationStatus === "success" && verificationResult?.carrier && (
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Verified: {verificationResult.carrier.legalName}
                          {verificationResult.allowedToOperate ? "" : " (Not allowed to operate)"}
                        </span>
                      </div>
                    )}

                    {verificationStatus === "error" && (
                      <div className="space-y-1">
                        {verificationResult?.errors && verificationResult.errors.length > 1 ? (
                          <div className="space-y-1">
                            {verificationResult.errors.map((err, i) => (
                              <div key={i} className="flex items-start gap-2 text-destructive">
                                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                <span className="text-sm">{err}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-destructive">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm">{verificationResult?.error || "Verification failed"}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Section 3 — Specializations */}
                <div className="border-t pt-5 pb-5 mb-5 space-y-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Specializations</p>

                  <div className="space-y-3">
                    <FormLabel>LTL Specializations</FormLabel>
                    <div className="grid grid-cols-2 gap-1.5">
                      {EQUIPMENT_SPECIALIZATION_OPTIONS.map((spec) => (
                        <div key={spec} className="flex items-center gap-2">
                          <Checkbox
                            id={`equip-${spec}`}
                            checked={specializations.includes(spec)}
                            onCheckedChange={() => toggleSpecialization(spec)}
                            data-testid={`checkbox-equipment-${spec}`}
                          />
                          <label htmlFor={`equip-${spec}`} className="text-sm cursor-pointer">
                            {spec}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <FormLabel>Commodity Specializations</FormLabel>
                    <div className="grid grid-cols-2 gap-1.5">
                      {COMMODITY_OPTIONS.map((spec) => (
                        <div key={spec} className="flex items-center gap-2">
                          <Checkbox
                            id={`commodity-${spec}`}
                            checked={commoditySpecializations.includes(spec)}
                            onCheckedChange={() => toggleCommoditySpecialization(spec)}
                            data-testid={`checkbox-commodity-${spec}`}
                          />
                          <label htmlFor={`commodity-${spec}`} className="text-sm cursor-pointer">
                            {spec}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Section 4 — Coverage */}
                <div className="border-t pt-5 pb-5 mb-5 space-y-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Coverage</p>

                  <div className="space-y-3">
                    <FormLabel>Operating Regions</FormLabel>
                    <div className="grid grid-cols-2 gap-1.5">
                      {REGION_OPTIONS.map((region) => (
                        <div key={region} className="flex items-center gap-2">
                          <Checkbox
                            id={`region-${region}`}
                            checked={regions.includes(region)}
                            onCheckedChange={() => toggleRegion(region)}
                            data-testid={`checkbox-region-${region}`}
                          />
                          <label htmlFor={`region-${region}`} className="text-sm cursor-pointer">
                            {region}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <FormLabel>Primary Shipping Lanes</FormLabel>
                    <div className="flex gap-2">
                      <Input
                        placeholder="e.g., Chicago to Dallas, NY to LA"
                        value={laneInput}
                        onChange={(e) => setLaneInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addShippingLane())}
                        data-testid="input-shipping-lane"
                      />
                      <Button type="button" onClick={addShippingLane} data-testid="button-add-lane">
                        Add
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {shippingLanes.map((lane) => (
                        <Badge key={lane} variant="secondary" className="gap-1" data-testid={`badge-lane-${lane}`}>
                          {lane}
                          <X className="w-3 h-3 cursor-pointer" onClick={() => removeShippingLane(lane)} />
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Section 5 — Acknowledgment */}
                <div className="border-t pt-5 pb-5 mb-5 space-y-3">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Acknowledgment</p>

                  <FormField
                    control={form.control}
                    name="hasBrokerBond"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-2 rounded-md bg-muted p-4">
                          <FormControl>
                            <Checkbox
                              id="hasBrokerBond"
                              checked={field.value === true}
                              onCheckedChange={(checked) => field.onChange(checked === true ? true : undefined)}
                              data-testid="checkbox-broker-bond"
                            />
                          </FormControl>
                          <label htmlFor="hasBrokerBond" className="text-sm font-medium cursor-pointer leading-snug">
                            I maintain an active freight broker surety bond (BMC-84) or trust fund agreement (BMC-85) of $75,000 as required by FMCSA, with documentation available upon request.
                          </label>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="border-t pt-5">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={mutation.isPending}
                    data-testid="button-complete-profile"
                  >
                    {mutation.isPending ? "Saving..." : "Complete Profile"}
                  </Button>
                </div>

              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
